import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { getStaffMembers } from '../services/wordpress';
import { NewsItem } from '../types';
import { Linkedin, Twitter, Quote as QuoteIcon } from 'lucide-react';
import './StaffPage.css';

const StaffPage: React.FC = () => {
    const [staff, setStaff] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStaff = async () => {
            try {
                const data = await getStaffMembers();
                setStaff(data);
            } catch (error) {
                console.error('Error loading staff:', error);
            } finally {
                setLoading(false);
            }
        };
        loadStaff();
    }, []);

    if (loading) {
        return (
            <div className="StaffPage_loading">
                <div className="StaffPage_spinner"></div>
            </div>
        );
    }

    return (
        <div className="StaffPage_container">
            <Helmet>
                <title>Tim Ahli & Operasional - Lakpesdam PBNU</title>
                <meta name="description" content="Tim ahli dan staf operasional Lembaga Kajian dan Pengembangan SDM PBNU yang berdedikasi." />
                <meta property="og:title" content="Tim Ahli & Operasional - Lakpesdam PBNU" />
                <meta property="og:description" content="Tim ahli dan staf operasional Lakpesdam PBNU." />
                <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />
                <meta property="og:type" content="website" />
            </Helmet>
            <header className="StaffPage_header">
                <div className="StaffPage_badges">
                    <span className="StaffPage_badge">TEAM</span>
                    <span className="StaffPage_badge">KAMI</span>
                </div>
                <h1 className="StaffPage_title">TIM AHLI & OPERASIONAL LAKPESDAM</h1>

                <div className="StaffPage_intro">
                    <div className="StaffPage_intro_left">
                        <p>
                            LAKPESDAM PBNU berkomitmen untuk mengelola pemberdayaan sumber daya manusia
                            dengan pendekatan yang inklusif dan berkelanjutan bagi seluruh warga Nahdliyin.
                        </p>
                    </div>
                    <div className="StaffPage_intro_right">
                        <p>
                            Kami membangun ekosistem yang mendukung inovasi dan kolaborasi antar lembaga.
                            Melalui tim yang berdedikasi, kami memastikan setiap program berjalan dengan
                            transparansi dan akuntabilitas tinggi untuk mencapai visi besar organisasi.
                        </p>
                        <p>
                            Setiap anggota tim kami membawa keahlian unik, mulai dari riset kebijakan,
                            pengembangan komunitas, hingga manajemen program strategis.
                        </p>
                    </div>
                </div>
            </header>

            <div className="StaffPage_grid">
                {staff.map((member, index) => {
                    const hasQuote = Boolean(member.quote);

                    // Komponen card standar untuk staff member
                    const normalCard = (
                        <motion.div
                            key={member.id}
                            className="StaffPage_card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="StaffPage_image_wrapper">
                                <img
                                    src={member.imageUrl || 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22600%22%20viewBox%3D%220%200%20600%20600%22%3E%3Crect%20fill%3D%22%23064e3b%22%20width%3D%22600%22%20height%3D%22600%22%2F%3E%3Ctext%20fill%3D%22%23ffffff%22%20font-family%3D%22sans-serif%22%20font-size%3D%2280%22%20dy%3D%2230%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EL%3C%2Ftext%3E%3C%2Fsvg%3E'}
                                    alt={member.title}
                                    className="StaffPage_img"
                                    loading="lazy"
                                    width="600"
                                    height="800"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22600%22%20viewBox%3D%220%200%20600%20600%22%3E%3Crect%20fill%3D%22%23064e3b%22%20width%3D%22600%22%20height%3D%22600%22%2F%3E%3Ctext%20fill%3D%22%23ffffff%22%20font-family%3D%22sans-serif%22%20font-size%3D%2280%22%20dy%3D%2230%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EL%3C%2Ftext%3E%3C%2Fsvg%3E';
                                    }}
                                />
                                <div className="StaffPage_overlay">
                                    <h3 className="StaffPage_name">{member.title}</h3>
                                    <p className="StaffPage_role">{member.role || 'Staf Operasional'}</p>
                                    {member.responsibility && (
                                        <div className="StaffPage_responsibility">
                                            <span>Tanggung Jawab:</span>
                                            <p>{member.responsibility}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );

                    // Jika ada quote, kembalikan both Highlight (Quote Box) & Normal Card
                    if (hasQuote) {
                        return (
                            <React.Fragment key={`fragment-${member.id}`}>
                                <motion.div
                                    className="StaffPage_card StaffPage_card_highlight"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="StaffPage_card_content">
                                        <div className="StaffPage_socials">
                                            <div className="StaffPage_social_icon"><Linkedin size={14} /></div>
                                            <div className="StaffPage_social_icon"><Twitter size={14} /></div>
                                        </div>
                                        <div className="StaffPage_quote_wrapper">
                                            <QuoteIcon className="StaffPage_quote_icon" size={24} />
                                            <blockquote className="StaffPage_quote">
                                                "{member.quote}"
                                            </blockquote>
                                        </div>
                                        <div className="StaffPage_info">
                                            <div className="StaffPage_name_pill">{member.title}</div>
                                            <div className="StaffPage_role">{member.role || 'Staf Operasional'}</div>
                                        </div>
                                    </div>
                                </motion.div>
                                {normalCard}
                            </React.Fragment>
                        );
                    }

                    return normalCard;
                })}
            </div>
        </div>
    );
};

export default StaffPage;
